/*
 * @Description: ECharts图表处理
 * @Author: mdb
 * @Date: 2022-11-25 10:41:42
 * @LastEditTime: 2022-11-29 09:03:45
 * @LastEditors: mdb
 */
import { ref, unref, computed, nextTick, watch, onMounted } from 'vue'
import { useDebounceFn, useTimeoutFn, useEventListener, tryOnUnmounted } from '@vueuse/core'
import echarts from '@/lib/echarts'
import type { Ref } from 'vue'
import type { EChartsOption } from 'echarts'

export function useECharts(elRef: Ref<HTMLDivElement>, theme: 'light' | 'dark' | 'default' = 'default', isResize = true) {
  const getDarkMode = computed(() => theme)
  let chartInstance: echarts.ECharts | null = null
  let resizeFn: Function = resize
  const cacheOptions = ref({}) as Ref<EChartsOption>
  let removeResizeFn: Function

  resizeFn = useDebounceFn(resize, 200)

  const getOptions = computed(() => {
    if (getDarkMode.value !== 'dark') {
      return cacheOptions.value as EChartsOption
    }
    return {
      backgroundColor: 'transparent',
      ...cacheOptions.value
    } as EChartsOption
  })

  function initCharts(t = theme) {
    const el = unref(elRef)
    if (!el || !unref(el)) {
      return
    }

    chartInstance = echarts.init(el, t)
    const removeEvent = useEventListener(window, 'resize', () => resizeFn())
    removeResizeFn = removeEvent
  }

  function setOptions(options: EChartsOption, clear = true) {
    cacheOptions.value = options
    if (unref(elRef)?.offsetHeight === 0) {
      useTimeoutFn(() => {
        setOptions(unref(getOptions))
      }, 30)
      return
    }
    nextTick(() => {
      useTimeoutFn(() => {
        if (!chartInstance) {
          initCharts(theme as 'default')

          if (!chartInstance) return
        }
        clear && chartInstance?.clear()

        chartInstance?.setOption(unref(getOptions))
      }, 30)
    })
  }

  function resize() {
    if (!isResize) { return }
    chartInstance?.resize({
      animation: {
        duration: 300,
        easing: 'quadraticIn'
      }
    })
  }
  onMounted(() => {
    const el = unref(elRef)
    if (!el) return
    const observer = new ResizeObserver(() => {
      resize()
    })
    observer.observe(el)
    tryOnUnmounted(() => {
      observer.disconnect()
    })
  })
  watch(
    () => getDarkMode.value,
    (theme) => {
      if (chartInstance) {
        chartInstance.dispose()
        initCharts(theme as 'default')
        setOptions(cacheOptions.value)
      }
    }
  )

  tryOnUnmounted(() => {
    if (!chartInstance) return
    removeResizeFn()
    chartInstance.dispose()
    chartInstance = null
  })

  function getInstance(): echarts.ECharts | null {
    if (!chartInstance) {
      initCharts(getDarkMode.value as 'default')
    }
    return chartInstance
  }

  return {
    setOptions,
    resize,
    echarts,
    getInstance
  }
}
